import { useCallback, useEffect, useMemo, useState } from "react";
import SDSRequest from "../../../../api/SDSRequest/SDSRequest";
import { Roles } from "../../../../constants/roles";
import { SDSRequestUserRole } from "../types";
import { requestStatusOptions } from "../constants";
import { useUser } from "../../../../context/UserContext";
import { useAuth } from "../../../../context/AuthContext";

export const useSdsRequestUserRoles = (request: SDSRequest) => {
  const { username } = useUser();
  const { hasAnyRole } = useAuth();
  const [sdsRequestUserRoles, setSdsRequestUserRoles] = useState<SDSRequestUserRole[]>([]);

  useEffect(() => {
    const roles: SDSRequestUserRole[] = [];

    if (username === request?.createdByEmail) {
      roles.push(SDSRequestUserRole.Creator);
    }
    if (username === request?.requestedByEmail) {
      roles.push(SDSRequestUserRole.Requester);
    }
    if (hasAnyRole([Roles.SdsRequestWorker, Roles.Developer])) {
      roles.push(SDSRequestUserRole.Worker);
    }

    setSdsRequestUserRoles(roles);
  }, [request, username, hasAnyRole]);

  const hasRole = useCallback(
    (role: SDSRequestUserRole): boolean => {
      return sdsRequestUserRoles.includes(role);
    },
    [sdsRequestUserRoles],
  );

  const canEdit = useMemo(() => {
    const requestIsNew = request === undefined;
    const requestIsEditable = [requestStatusOptions.draft, requestStatusOptions.submitted].includes(
      request?.requestStatusName,
    );
    const userIsCreatorOrRequester =
      hasRole(SDSRequestUserRole.Creator) || hasRole(SDSRequestUserRole.Requester);

    return requestIsNew || (userIsCreatorOrRequester && requestIsEditable);
  }, [request, hasRole]);

  const canAssign = useMemo(() => {
    const requestIsAssignable = [
      requestStatusOptions.submitted,
      requestStatusOptions.assigned,
    ].includes(request?.requestStatusName);

    return requestIsAssignable && hasRole(SDSRequestUserRole.Worker);
  }, [request, hasRole]);

  const canProcess = useMemo(() => {
    const requestIsProcessable = [
      requestStatusOptions.submitted,
      requestStatusOptions.assigned,
      requestStatusOptions.inProgress,
      requestStatusOptions.onHold,
    ].includes(request?.requestStatusName);

    return requestIsProcessable && hasRole(SDSRequestUserRole.Worker);
  }, [request, hasRole]);

  return { sdsRequestUserRoles, canEdit, canAssign, canProcess };
};
